<div class="loading-container" [class.hidden]="!isFirstLoad()">
    <p class="loading-container__message">Hang tight!</p>
    <div class="loading-container__spinner">
        <mat-spinner data-qa-id="recruiter-qualified-candidates-spinner" diameter="44" class="custom-spinner"></mat-spinner>
    </div>
    <p class="loading-container__message">We're working on finding the perfect match!</p>
    <p class="loading-container__message">The right candidate is worth the wait&hellip;</p>
</div>
<div [class.hidden]="isLoading() && isFirstLoad()">
    <div class="total-candidates-banner">
        <span class="total-candidates" data-qa-id="recruiter-qualified-candidates-recommended">Recommended Candidates ({{ totalCandidates() }})</span>
    </div>

    <div class="info-banner">
        <mat-icon class="info-icon" data-qa-id="recruiter-qualified-candidates-info-icon">info</mat-icon>
        <span class="information-text" data-qa-id="recruiter-qualified-candidates-info-text">
            @if(hasCandidates()) {
            This list shows candidates from your Hot list who may be a good fit for this role based on their
            qualifications and profile information. The match rating estimates how well a candidate fits the job
            requirements and is intended as a guide. It does not account for every factor.
            }
            @else{
                No recommended candidates are currently available. This may be due to missing or overly restrictive requirements.
            }
        </span>
    </div>
</div>
<div [class.hidden]="!hasCandidates()">
    <nova-grid #grid class="recruiter-qualified-candidates-grid"
        [data]="data()"
        [needToolbar]="true"
        [needRefresh]="false"
        [toolbarTemplate]="toolbarTemplate"
        [needToolbarColumnShowHideMenu]="false"
        [isLoading]="isLoading()"
        [filterable]="false"
        (rowSelected)="onRowSelected($event)"
        (dataStateChange)="onDataStateChange($event)"
        [scrollable]="'none'"
        [pageable]="false"
        [selectable]="selectableSettings()"
        selectionkey="candidateUserId"
        [resizable]="false">
        <ng-template #toolbarTemplate>
            <div class="remove-candidates">
                @if (enabledRecommendedJobs()) {
                    <button mat-flat-button
                            color="primary"
                            data-qa-id="recruiter-recommend-job"
                            class="recommend-job-btn"
                            (click)="onRecommendJobClick()"
                            [disabled]="!selectedCandidateUserIds()?.length || isProcessingCandidateRemovals()">
                            RECOMMEND JOB
                    </button>
                    <button mat-flat-button
                            data-qa-id="recruiter-remove-candidates"
                            color="secondary"
                            (click)="onClickRemoveCandidates()"
                            [disabled]="!selectedCandidateUserIds()?.length || isProcessingCandidateRemovals()">
                            REMOVE CANDIDATE(S)
                    </button>
                } @else {
                    <button mat-flat-button
                            data-qa-id="recruiter-remove-candidates"
                            color="primary"
                            (click)="onClickRemoveCandidates()"
                            [disabled]="!selectedCandidateUserIds()?.length || isProcessingCandidateRemovals()">
                            REMOVE CANDIDATE(S)
                    </button>
                }
            </div>
        </ng-template>

        <!-- CANDIDATE -->
        <nova-grid-column field="candidate" title="Candidate" [sortable]="isEnableSorting()"
            [template]="candidateTemplate" [filterable]="false">
            <ng-template #candidateTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                <div class="cell">
                    @if(dataItem.candidateFullName) {
                    <a [attr.data-cy]="'candidate-name-' + rowIndex + '-' + dataItem.candidateUserId"
                        [routerLink]="['/recruiting/candidates/', dataItem.candidateUserId, 'new-profile', 'about']"
                        target="_blank">
                        {{ dataItem.candidateFullName }}
                    </a>
                    }
                </div>
            </ng-template>
        </nova-grid-column>

        @if (enabledAdditionalColumns()) {
            <!-- LICENSES -->
            <nova-grid-column field="formattedLicenses" title="Licenses" [template]="licensesTemplate" [sortable]="false" [filterable]="false">
                <ng-template #licensesTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                    <div class="cell lines-cell">
                        @if(dataItem.formattedLicenses) {
                            @for(license of dataItem.formattedLicenses; track $index) {
                                <div>{{ license }}</div>
                            }
                        }
                    </div>
                </ng-template>
            </nova-grid-column>

            <!-- Expertises -->
            <nova-grid-column field="formattedExpertises" title="Specialty" [template]="expertisesTemplate" [sortable]="false" [filterable]="false">
                <ng-template #expertisesTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                    <div class="cell lines-cell">
                        @if(dataItem.formattedExpertises) {
                            @for(expertise of dataItem.formattedExpertises; track $index) {
                                <div>{{ expertise }}</div>
                            }
                        }
                    </div>
                </ng-template>
            </nova-grid-column>
        }

        <!-- MATCH PERCENT -->
        <nova-grid-column field="matchCategory" title="Match Rating" [template]="matchPercentageTemplate"
            [filterable]="false" [sortable]="isEnableSorting() && hasAnyValue('matchCategory')">
            <ng-template #matchPercentageTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                @if (dataItem.matchPercentage && dataItem.candidateUserId) {
                <div [ngClass]="dataItem?.matchColor" class="match-category">
                    <span class="nova__link" (click)="openBreakdownDialog(dataItem)">
                        {{ dataItem.matchCategory }}
                    </span>
                </div>
                }
            </ng-template>
        </nova-grid-column>

        @if (enabledAdditionalColumns()) {
            <!-- CONTRACT END DATE -->
            <nova-grid-column field="contractEndDate" title="Contract End Date" [sortable]="isEnableSorting() && hasAnyValue('contractEndDate')"
                [template]="contractEndDateTemplate" [filterable]="false">
                <ng-template #contractEndDateTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                    @if (dataItem.contractEndDate) {
                        <div class="cell">{{ dataItem.contractEndDate | date: 'MM/dd/yyyy' }}</div>
                    }
                </ng-template>
            </nova-grid-column>
        }

        <!-- AVAILABLE DATE -->
        <nova-grid-column field="availableDate" title="Available" [sortable]="isEnableSorting() && hasAnyValue('availableDate')"
            [template]="availableDateTemplate" [filterable]="false">
            <ng-template #availableDateTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                @if (dataItem.availableDate) {
                <div class="cell">{{ dataItem.availableDate | date: 'MM/dd/yyyy' }}</div>
                }
            </ng-template>
        </nova-grid-column>

        <!-- RECOMMENDED DATE -->
        <nova-grid-column field="recommendedDate" title="Recommended" [sortable]="isEnableSorting() && hasAnyValue('recommendedDate')"
            [template]="recommendedDateTemplate" [filterable]="false">
            <ng-template #recommendedDateTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                @if (dataItem.recommendedDate ) {
                <div class="cell">{{ dataItem.recommendedDate | date: 'MM/dd/yyyy' }}</div>
                }
            </ng-template>
        </nova-grid-column>

        <!-- INTERESTED DATE -->
        <nova-grid-column field="interestedDate" title="Interested" [sortable]="isEnableSorting() && hasAnyValue('interestedDate')"
            [template]="interestedDateTemplate" [filterable]="false">
            <ng-template #interestedDateTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                @if (dataItem.interestedDate) {
                <div class="cell">{{ dataItem.interestedDate | date: 'MM/dd/yyyy' }}</div>
                }
            </ng-template>
        </nova-grid-column>

        <!-- LAST CONTACTED DATE -->
        <nova-grid-column field="lastContacted" title="Last Contacted" [sortable]="isEnableSorting() && hasAnyValue('lastContacted')"
            [template]="lastContactedTemplate" [filterable]="false">
            <ng-template #lastContactedTemplate let-dataItem="dataItem" let-rowIndex="rowIndex">
                @if (dataItem.lastContacted) {
                <div class="cell">{{ dataItem.lastContacted | date: 'MM/dd/yyyy' }}</div>
                }
            </ng-template>
        </nova-grid-column>
    </nova-grid>
</div>
